// components/dashboard/ProjectTable.jsx

import React, { useMemo } from "react";
import { useAuth } from "@/hooks/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProjectTableRow } from "./ProjectTableRow";
import { Skeleton } from "@/components/ui/skeleton";

const PROJECT_STATUSES = ["ACTIVE", "INACTIVE", "COMPLETED", "UNREALISED", "UPCOMING"];
const STATUS_ORDER = { ACTIVE: 0, INACTIVE: 1, COMPLETED: 2, UNREALISED: 3 };

const LoadingSkeleton = ({ columns }) =>
  Array.from({ length: 5 }).map((_, i) => (
    <TableRow key={i}>
      {Array.from({ length: columns }).map((_, j) => (
        <TableCell key={j}>
          <Skeleton className="h-6 w-full" />
        </TableCell>
      ))}
    </TableRow>
  ));

export function ProjectTable({ projects, isLoading, actions }) {
  console.log("projects-------------", projects);
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const showFinance = user?.role !== "TEAM";

  const projectsByStatus = useMemo(() => {
    const sorted = [...projects].sort(
      (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
    );
    return {
      ACTIVE: sorted.filter((p) => p.status === "ACTIVE"),
      INACTIVE: sorted.filter((p) => p.status === "INACTIVE"),
      COMPLETED: sorted.filter((p) => p.status === "COMPLETED"),
      UNREALISED: sorted.filter((p) => p.status === "UNREALISED"),
      UPCOMING: sorted.filter((p) => p.status === "UPCOMING"),
    };
  }, [projects]);

  const columns = useMemo(() => {
    let count = 6; // Base columns
    if (showFinance) count += 6;
    if (isAdmin) count += 1;
    return count;
  }, [showFinance, isAdmin]);

  const renderTableContent = (status) => {
    const projectList = projectsByStatus[status];
    console.log("projectList", projectList);

    if (isLoading) {
      return <LoadingSkeleton columns={columns} />;
    }

    if (projectList.length === 0) {
      return (
        <TableRow>
          <TableCell
            colSpan={columns}
            className="h-24 text-center text-muted-foreground"
          >
            No projects found in this category.
          </TableCell>
        </TableRow>
      );
    }

    return projectList.map((project) => (
      <ProjectTableRow
        key={project.project_id}
        project={project}
        onUpdate={actions.updateProject}
        onDelete={actions.deleteProject}
        onSaved={actions.refresh}
      />
    ));
  };

  return (
    <Card className="flex flex-col h-full">
      <CardHeader>
        <CardTitle>Projects</CardTitle>
      </CardHeader>
      <CardContent className="flex-grow flex flex-col">
        <Tabs defaultValue="ACTIVE" className="w-full flex-grow flex flex-col">
          <TabsList className="w-fit">
            {PROJECT_STATUSES.map((status) => (
              <TabsTrigger key={status} value={status}>
                {status.charAt(0) + status.slice(1).toLowerCase()}
                <span className="ml-2 h-5 min-w-[20px] px-1.5 flex items-center justify-center rounded-full bg-muted text-muted-foreground text-xs">
                  {projectsByStatus[status]?.length ?? 0}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
          {PROJECT_STATUSES.map((status) => (
            <TabsContent
              key={status}
              value={status}
              className="flex-grow mt-4 relative"
            >
              {/* This div is crucial for sticky header/columns to work within a container */}
              <div className="h-full overflow-auto">
                <Table className="relative">
                  {" "}
                  {/* `relative` is needed for sticky positioning context */}
                  <TableHeader>
                    <TableRow>
                      {/* --- STICKY COLUMNS START --- */}
                      <TableHead>Code</TableHead>
                      <TableHead>Name</TableHead>
                      {/* --- STICKY COLUMNS END --- */}

                      <TableHead>Client</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Client Company</TableHead>
                      <TableHead>GST Details</TableHead>
                      {(user?.role === "ADMIN" || user?.role === "PM") && (
                        <TableHead>Status</TableHead>
                      )}

                      <TableHead>Stage</TableHead>

                      {showFinance && (
                        <>
                          <TableHead className="text-right">
                            Fee (Total)
                          </TableHead>
                          <TableHead className="text-right">
                            Fees Received
                          </TableHead>
                          <TableHead className="text-right">
                            Outstanding
                          </TableHead>
                          <TableHead className="text-right">
                            Total Hours
                          </TableHead>
                          <TableHead className="text-right">
                            Direct Cost
                          </TableHead>
                          <TableHead className="text-right">Overhead</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                        </>
                      )}

                      {/* --- STICKY ACTION COLUMN --- */}
                      {isAdmin && (
                        <TableHead className="sticky right-0 bg-card z-10 text-center w-[200px] border-l">
                          Actions
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>{renderTableContent(status)}</TableBody>
                </Table>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
